import { useEffect, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { arrivedTotals, type ArrivedTotals } from '@/lib/donation-arrivals';
import { WINDOW_REFETCH_MS } from '@/lib/live-window';

const TICK_MS = 50;

/** Below the server's 10-minute cache, so the interval always finds the query stale enough to refetch. */
const STALE_MS = 60 * 1000;

/** Worth one window, so not kept under `gcTime: Infinity`; three windows of headroom for a background tab (TEC-3807). */
const GC_MS = 15 * 60 * 1000;

/**
 * ONE hook for both figures, so tiles never disagree (`lib/donation-arrivals.ts`). The key is stable
 * because the server derives the window (`lib/live-window.ts`); `now` starts at 0, or React #418.
 */
export function useLiveDonationTotals(): ArrivedTotals {
	const { data } = useSuspenseQuery(
		trpc.portal.getRecentDonations.queryOptions(
			{ mode: 'day' },
			{
				staleTime: STALE_MS,
				gcTime: GC_MS,
				refetchInterval: WINDOW_REFETCH_MS,
				// The counter is the point of its page; a returning tab should find it current.
				refetchIntervalInBackground: false,
			},
		),
	);

	const [now, setNow] = useState(0);
	const donations = data.donations;

	useEffect(() => {
		setNow(Date.now());
		if (donations.length === 0) return;
		// Otherwise it ticks 20 times a second recomputing a total that cannot change.
		if (donations.every((donation) => donation.timestamp <= Date.now())) return;
		const timer = setInterval(() => setNow(Date.now()), TICK_MS);
		return () => clearInterval(timer);
	}, [donations]);

	return arrivedTotals(data, donations, now);
}
